/** Tool definitions: Target allocation profiles. */

import type { ToolDefinition } from './types.js';

export const targetProfilesTools = {
  list_target_profiles: {
    name: 'list_target_profiles',
    access: 'read',
    description:
      'List all saved target allocation profiles for the user. Each profile is a named target portfolio allocation (used by the rebalancer) with a list of items, where each item is a bucket of one or more symbols at a target percentage. Returns id, name, description, notes, items, createdAt, updatedAt.',
    inputSchema: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
  get_target_profile: {
    name: 'get_target_profile',
    access: 'read',
    description:
      'Get a single target allocation profile by id or name. Either id or name is required.',
    inputSchema: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'Profile UUID.' },
        name: { type: 'string', description: 'Profile name (case-sensitive). Used if id is not provided.' },
      },
      required: [],
    },
  },
  create_target_profile: {
    name: 'create_target_profile',
    access: 'write',
    description:
      "Save a target portfolio allocation as a named profile for the rebalancer. Each item is a bucket: { label, symbols: string[], targetPercent }.\n\n" +
      "Item shapes by symbols length:\n" +
      "  • 1 symbol  — single-ticker line (e.g., VTI 30%).\n" +
      "  • 2+ symbols — multi-symbol bucket (e.g., RHP+VNQ at 3%, IREN+APLD+OKLO at 10%). On apply, the bucket's percent is split across matched current holdings proportional to their value.\n" +
      "  • 0 symbols — catch-all bucket (max ONE per profile). At apply time it absorbs every current holding NOT claimed by an explicit bucket. Use this for an 'Other' or 'Speculative' bucket where you don't want to enumerate every ticker.\n\n" +
      "Sum of all targetPercent values must be ≤ 100. Anything under 100 is implicit cash.\n\n" +
      "Example translating a user's allocation table:\n" +
      "  US Total Stock / VTI / 30%        → { label: 'US Total Stock', symbols: ['VTI'], targetPercent: 30 }\n" +
      "  REITs / RHP + VNQ / 3%            → { label: 'REITs', symbols: ['RHP','VNQ'], targetPercent: 3 }\n" +
      "  Cash / VUSXX or VMFXX / 13%       → { label: 'Cash', symbols: ['VUSXX','VMFXX'], targetPercent: 13 }\n" +
      "  Speculative (any other holdings) / 10%  → { label: 'Speculative', symbols: [], targetPercent: 10 }",
    inputSchema: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Profile name. Must be unique per user.',
        },
        description: {
          type: 'string',
          description: 'Optional one-line description.',
        },
        notes: {
          type: 'string',
          description: 'Optional free-text notes (e.g., rationale, source).',
        },
        items: {
          type: 'array',
          description: 'Array of profile items. Each item is a bucket.',
          items: {
            type: 'object',
            properties: {
              label: {
                type: 'string',
                description: 'Display label (e.g., "US Total Stock", "REITs", "Speculative").',
              },
              symbols: {
                type: 'array',
                items: { type: 'string' },
                description: 'Tickers in this bucket. Empty array = catch-all (max one per profile).',
              },
              targetPercent: {
                type: 'number',
                description: 'Bucket\'s share of total portfolio (0–100).',
              },
            },
            required: ['label', 'symbols', 'targetPercent'],
          },
        },
      },
      required: ['name', 'items'],
    },
  },
  update_target_profile: {
    name: 'update_target_profile',
    access: 'write',
    description:
      'Update fields on an existing target allocation profile. Only provided fields are changed. Pass an empty string in description/notes to clear them. Pass items to replace the entire item list (partial updates of items are not supported — fetch first, modify, then send the full array).',
    inputSchema: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'Profile UUID.' },
        name: { type: 'string' },
        description: { type: 'string' },
        notes: { type: 'string' },
        items: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              label: { type: 'string' },
              symbols: { type: 'array', items: { type: 'string' } },
              targetPercent: { type: 'number' },
            },
            required: ['label', 'symbols', 'targetPercent'],
          },
        },
      },
      required: ['id'],
    },
  },
  apply_target_profile: {
    name: 'apply_target_profile',
    access: 'read',
    description:
      "Apply a saved target allocation profile to a portfolio's current holdings, exactly as the web rebalancer's \"Apply profile\" does, without saving or trading anything. " +
      'Returns per-holding target percentages (holdingTargets, keyed by holdingId; new positions use ids like "profile-new::VXUS::<accountId>"), ' +
      'the holdings the profile changes (modifiedHoldingIds), new positions for listed symbols not currently held (newPositions, placed in the largest selected account), ' +
      'held positions left at 0% (willBeSold), listed symbols with no quote that were dropped (unquotedSymbols), and — unless includeTrades is false — ' +
      'the proposed whole-share trades that would reach those targets (trades, from the same calculator as the rebalancer).',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: { type: 'string', description: 'Portfolio ID.' },
        profileId: { type: 'string', description: 'Profile UUID.' },
        profileName: { type: 'string', description: 'Profile name (case-sensitive). Used if profileId is not provided.' },
        accountIds: {
          type: 'array',
          items: { type: 'string' },
          description: "Optional account IDs to rebalance within. Defaults to all of the portfolio's accounts.",
        },
        includeTrades: {
          type: 'boolean',
          description: 'Also calculate the proposed trades. Defaults to true.',
        },
      },
      required: ['portfolioId'],
    },
  },
  delete_target_profile: {
    name: 'delete_target_profile',
    access: 'write',
    description: 'Delete a target allocation profile by id.',
    inputSchema: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'Profile UUID.' },
      },
      required: ['id'],
    },
  },
} satisfies Record<string, ToolDefinition>;
